import { type Duration, type Pitch, splitTicks, barCapacity, durationForTicks } from '@simplenotation/theory';
import type { Beat, Note, Score, Track, TrackBar, BarInfo, TimeSignature } from './types';
import { SCORE_VERSION } from './types';
import { instrumentDef } from './instruments';

const ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
/** Short random id, unique enough within a document. */
export function newId(len = 8): string {
  let s = '';
  const c = globalThis.crypto;
  if (c?.getRandomValues) {
    const buf = new Uint8Array(len);
    c.getRandomValues(buf);
    for (const b of buf) s += ALPHABET[b % ALPHABET.length];
  } else {
    for (let i = 0; i < len; i++) s += ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
  }
  return s;
}

export function createNote(pitch: Pitch, extra: Partial<Note> = {}): Note {
  return { id: newId(), pitch, ...extra };
}

export function createRest(duration: Duration): Beat {
  return { id: newId(), duration: { ...duration }, rest: true, notes: [] };
}

export function createBeat(duration: Duration, notes: Note[]): Beat {
  return { id: newId(), duration: { ...duration }, rest: notes.length === 0, notes };
}

/** Rest beats filling a whole bar of a time signature (a single whole/dotted rest when possible). */
export function barRests(ts: TimeSignature): Beat[] {
  const cap = barCapacity(ts);
  const exact = durationForTicks(cap);
  if (exact) return [createRest(exact)];
  return splitTicks(cap).map((d) => createRest(d));
}

export function emptyTrackBar(ts: TimeSignature): TrackBar {
  return { voices: [barRests(ts)] };
}

export interface CreateTrackOptions {
  instrument?: string;
  name?: string;
  showStaff?: boolean;
  showTab?: boolean;
  tuning?: number[];
  capo?: number;
}

export function createTrack(barCount: number, ts: TimeSignature, opts: CreateTrackOptions = {}): Track {
  const def = instrumentDef(opts.instrument ?? 'classical-guitar');
  const fretted = !!def.tuning;
  return {
    id: newId(),
    name: opts.name ?? def.name.replace(/ \(.*\)$/, ''),
    shortName: undefined,
    instrument: def.id,
    sound: def.sound,
    writtenToSounding: def.writtenToSounding,
    clef: def.clef,
    tuning: opts.tuning ?? (def.tuning ? [...def.tuning] : undefined),
    capo: opts.capo ?? (fretted ? 0 : undefined),
    showStaff: opts.showStaff ?? true,
    showTab: opts.showTab ?? (fretted && !!def.showTab),
    tabStems: false,
    visualizations: [...(def.visualizations ?? [])],
    volume: 0.8,
    bars: Array.from({ length: barCount }, () => emptyTrackBar(ts)),
  };
}

export interface CreateScoreOptions {
  title?: string;
  bars?: number;
  timeSig?: TimeSignature;
  fifths?: number;
  bpm?: number;
  instrument?: string;
}

export function createScore(opts: CreateScoreOptions = {}): Score {
  const ts = opts.timeSig ?? { num: 4, den: 4 };
  const n = Math.max(1, opts.bars ?? 4);
  const bars: BarInfo[] = Array.from({ length: n }, (_, i) =>
    i === 0 ? { timeSig: { ...ts }, keySig: { fifths: opts.fifths ?? 0, mode: 'major' } } : {},
  );
  bars[n - 1].barline = 'final';
  return {
    v: SCORE_VERSION,
    meta: { title: opts.title ?? 'Untitled' },
    bars,
    tempos: [{ bar: 0, beatOffsetTicks: 0, bpm: opts.bpm ?? 120, beatUnit: 4 }],
    tracks: [createTrack(n, ts, { instrument: opts.instrument })],
  };
}

/** Deep clone via JSON (documents are plain JSON). */
export function cloneJson<T>(x: T): T {
  return JSON.parse(JSON.stringify(x)) as T;
}

/** Give every beat and note a fresh id (after paste/duplicate). */
export function reId<T extends Beat>(b: T): T {
  return { ...b, id: newId(), notes: b.notes.map((n) => ({ ...n, id: newId() })) };
}
